// src/modules/prode.js — pantalla "Prode" de la SPA.
//
// FASE 4 · Bloque "Prode importado" (21-may-2026).
//
// Importación literal de fixture-sandbox.html (la herramienta de fixture del
// dueño) como pantalla de la SPA. NO se reinventa nada visual: el HTML, el
// CSS y el JS vienen del sandbox tal cual. Lo único que cambia: los mocks de
// datos se reemplazan por queries reales a Supabase vía src/core/fixture-db.js.
//
// Aislamiento: el sandbox define estilos globales (html, body, *, .subtitle…)
// que chocarían con la SPA. Para evitarlo, todo se monta dentro de un
// **Shadow DOM** — el CSS del sandbox entra verbatim y queda 100% aislado.
// Por eso `:root` del sandbox pasa a `:host` y los selectores html/body se
// funden en `:host`. El JS apunta sus queries al shadow root en vez de
// `document` (decisión zona amarilla del bloque).
//
// fixture-sandbox.html original NO se toca — sigue siendo herramienta admin.
//
// FASE 4 · F1.3.c2: las predicciones del Prode persisten en Postgres (tabla
// `predicciones`, vía src/core/predicciones.js). El sandbox NO se rediseña: la
// persistencia se engancha SOLO en el hook del botón "Guardar" (handler
// [data-save] de bindEvents → handleSave) y al montar se siembran las
// predicciones guardadas en S.predictions. Cierra DT-R.

import { fetchPartidos, fetchEquipos, fetchGruposMundial, fetchPosicionesGrupo } from '../core/fixture-db.js'
import { getPredicciones, upsertPrediccion } from '../core/predicciones.js'
import { getMembership } from '../core/groups.js'
import { renderActiveGroupBar, renderBottomNav } from '../ui/components.js'
import { withTimeout, NET_TIMEOUT_MS } from '../utils/timeout.js'
import { appRoot } from '../utils/dom.js'
import { buildProdeSkeleton } from './prode-skeleton.js'
import { formatHoraART, fechaKeyART, formatFechaLargaART } from '../utils/fecha-art.js'
import { lineasEstadoVivo } from '../utils/estado-partido.js'
import {
    PLAYOFF_CARD_CSS,
    renderPlayoffCardProde,
    wirePlayoffCard,
    decodePrediccionPlayoff,
} from '../ui/playoff-prediction-card.js'

// ─── CSS del sandbox (verbatim; :root/html/body → :host para el Shadow DOM) ──

// Exportado para reuso desde otros módulos (perfil-prode.js → src/ui/match-card.js).
// Cierra Bug B (visual roto en perfil-prode por falta de Shadow DOM con este CSS).
export const PRODE_CSS = `
:host {
    --bg: #08111d;
    --surface: rgba(255,255,255,.055);
    --surface-soft: rgba(255,255,255,.035);
    --line: rgba(255,255,255,.105);
    --white: #f8fafc;
    --muted: #94a3b8;
    --blue: #75aadb;
    --green: #3ddc84;
    --gold: #f6c453;
    --red: #ef4444;
    --ink: #06111f;
    --font: "Inter", system-ui, sans-serif;
    --heading: "Outfit", system-ui, sans-serif;
    --team-name-normal: 13.5px;
    display: block;
    color: var(--white);
    font-family: var(--font);
    min-height: 100vh;
    overflow-x: hidden;
    background:
        radial-gradient(circle at 10% -8%, rgba(117,170,219,.24), transparent 30%),
        linear-gradient(180deg, #07101b, #0a1220 48%, #070b12);
}

* { box-sizing: border-box; }
button, select { font: inherit; }
button { cursor: pointer; }
.match > summary { cursor: default; }
.match > summary button { cursor: pointer; }

.screen { width: min(100%, 760px); max-width: 100vw; overflow-x: clip; margin: 0 auto; padding: 18px 14px 90px; }
.hero { padding: 10px 2px 14px; }
.brand { display: inline-flex; align-items: center; min-height: 24px; padding: 0 9px; border: 1px solid rgba(117,170,219,.28); border-radius: 999px; color: var(--blue); background: rgba(117,170,219,.08); font-size: 10px; font-weight: 900; text-transform: uppercase; }
h1 { margin: 10px 0 4px; font: 900 clamp(30px, 9vw, 50px)/.92 var(--heading); text-transform: uppercase; }
.subtitle { margin: 0; color: var(--muted); font-size: 14px; line-height: 1.35; }
/* Título de la pantalla, debajo del encabezado común (renderActiveGroupBar en
   light DOM) y encima de los tabs. REUSA el estilo del bloque "Mi Prode Historial"
   del Perfil (.perfil-prode-cta en css/perfil-runtime.css): mismo gradiente azul
   claro vibrante, color de texto #06111f, border-radius 18px y box-shadow. Como
   el título vive en Shadow DOM (CSS aislado), se copian los VALORES (no se puede
   heredar la clase global). Diferencias justificadas: padding horizontal recortado
   (el texto es largo, 36 chars) y SIN text-transform:uppercase (la capitalización
   "Partido a Partido + Tus Predicciones" es literal). font-size con clamp + white-
   space:nowrap → entra SIEMPRE en un renglón (~320-430px mobile) sin partirse ni
   deformar; el font-size es el único parámetro de ajuste. */
.prode-screen-title { display: block; margin: 2px 0 12px; padding: 0.85rem 0.7rem; border: 0; border-radius: 18px; background: linear-gradient(135deg, #5e93c2 0%, #75aadb 52%, #9bc4e6 100%); box-shadow: 0 16px 34px rgba(117,170,219,.2), 0 0 24px rgba(117,170,219,.22); color: #06111f; font-family: var(--heading); font-weight: 900; font-size: clamp(13px, 4.3vw, 19px); line-height: 1.15; text-align: center; white-space: nowrap; overflow: hidden; }

.chips { display: flex; gap: 7px; overflow-x: auto; padding: 12px 0 2px; scrollbar-width: none; }
.chips::-webkit-scrollbar { display: none; }
.chip { flex: 0 0 auto; display: inline-flex; align-items: center; min-height: 26px; padding: 0 9px; border: 1px solid var(--line); border-radius: 999px; background: rgba(255,255,255,.04); color: var(--muted); font-size: 11px; font-weight: 900; }
.chip strong { color: var(--white); margin-right: 4px; }

.tabs, .subtabs { display: grid; gap: 6px; width: 100%; min-width: 0; max-width: 100%; overflow: hidden; padding: 5px; border: 1px solid var(--line); border-radius: 13px; background: rgba(8,17,29,.92); backdrop-filter: blur(12px); }
.tabs { position: sticky; top: 0; z-index: 20; grid-template-columns: repeat(3, minmax(0,1fr)); margin: 10px 0 12px; }
.subtabs { grid-template-columns: repeat(2, minmax(0,1fr)); margin-bottom: 10px; position: static; }
.tab, .subtab { width: 100%; min-width: 0; min-height: 40px; overflow: hidden; border: 0; border-radius: 9px; background: transparent; color: var(--muted); font-size: 12px; font-weight: 900; line-height: 1.05; text-overflow: ellipsis; }
.tab.active, .subtab.active { color: var(--ink); background: linear-gradient(135deg, #a9d5f7, var(--blue)); }
/* Verde mint apagado, OTRO escalón más sutil/desaturado (sage). Histórico:
   #bbf7d0→#6ee7b7 (original) → #a5dcbb→#58c79e → ahora #a3ccb2→#5aab8e. Local a
   estos tabs (Evolución Mundial + sub-tabs Posiciones grupos / Llave); NO toca los
   otros usos de #bbf7d0 en la app. Texto #06201c conserva alto contraste. */
.tab.info.active { color: #06201c; background: linear-gradient(135deg, #a3ccb2, #5aab8e); }
.subtabs.info .subtab.active { color: #06201c; background: linear-gradient(135deg, #a3ccb2, #5aab8e); }
.subtabs.playoff-tabs { grid-template-columns: repeat(5, minmax(0,1fr)); }
.subtabs.playoff-tabs .subtab { font-size: 10.5px; }

.panel, .subpanel { display: none; gap: 10px; min-width: 0; max-width: 100%; overflow-x: clip; }
.panel.active, .subpanel.active { display: grid; }
.section-title { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 2px 0; }
.section-title h2 { margin: 0; font: 900 17px/1 var(--heading); text-transform: uppercase; }
.section-title span { color: var(--blue); font-size: 12px; font-weight: 900; white-space: nowrap; }

details { border: 1px solid var(--line); border-radius: 12px; background: var(--surface); overflow: hidden; }
details[open] { border-color: rgba(117,170,219,.26); background: radial-gradient(circle at 8% 0%, rgba(117,170,219,.11), transparent 36%), var(--surface); }
summary { list-style: none; cursor: pointer; }
summary::-webkit-details-marker { display: none; }
.fold-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 12px; }
.fold-head strong { font-size: 14px; }
.fold-head span { color: var(--muted); font-size: 12px; font-weight: 800; }
.fold-flags { display: flex; gap: 4px; align-items: center; }
.mini-flag { width: 24px; height: 17px; border-radius: 4px; border: 1px solid rgba(255,255,255,.12); object-fit: cover; background: rgba(255,255,255,.06); }
.fold-body { display: grid; gap: 8px; padding: 0 10px 10px; }

.match { border: 1px solid var(--line); border-radius: 11px; background: linear-gradient(135deg, rgba(255,255,255,.06), rgba(255,255,255,.025)); overflow: hidden; }
.match[open] { border-color: rgba(117,170,219,.24); }
.match.locked { opacity: .82; background: var(--surface-soft); }
.match-head { padding: 8px 9px 7px; }
.match-topline { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 5px; }
.match-meta { min-width: 0; color: rgba(148,163,184,.8); font-size: 9px; font-weight: 900; letter-spacing: .02em; text-transform: uppercase; white-space: nowrap; }
.match-meta strong { color: rgba(248,250,252,.72); font-weight: 900; }
.match-status { flex: 0 0 auto; display: grid; gap: 2px; text-align: right; color: rgba(248,250,252,.62); font-size: 9px; font-weight: 900; letter-spacing: .02em; text-transform: uppercase; }
.match-status.live { color: var(--gold); }
.status-detail { color: rgba(148,163,184,.9); font-size: 9px; font-weight: 800; letter-spacing: 0; text-transform: none; }
.teams { display: grid; grid-template-columns: minmax(72px,1fr) 28px 68px 28px minmax(72px,1fr); align-items: center; column-gap: 5px; width: min(100%, 430px); margin: 0 auto; }
.team-name.home { text-align: right; }
.team-name.away { text-align: left; }
.team-name { min-width: 0; color: var(--white); font-size: var(--team-name-normal); font-weight: 900; letter-spacing: normal; white-space: nowrap; }
.team-name-long { font-size: calc(var(--team-name-normal) * .88); letter-spacing: -.02em; }
.team-name-extra-long { font-size: calc(var(--team-name-normal) * .78); letter-spacing: -.03em; }
.flag { flex: 0 0 auto; width: 28px; height: 20px; border-radius: 5px; border: 1px solid rgba(255,255,255,.12); object-fit: cover; background: rgba(255,255,255,.07); }
.flag-code { flex: 0 0 auto; min-width: 30px; height: 20px; display: inline-flex; align-items: center; justify-content: center; border-radius: 5px; border: 1px solid rgba(117,170,219,.18); color: var(--blue); background: rgba(117,170,219,.07); font-size: 9px; font-weight: 900; }
.score-wrap { display: grid; justify-items: center; gap: 2px; }
.score { color: var(--gold); font: 900 21px/1 var(--heading); text-align: center; white-space: nowrap; letter-spacing: .01em; }
.time { color: var(--white); font: 900 15px/1 var(--heading); text-align: center; text-transform: uppercase; white-space: nowrap; }
.penalties { color: rgba(148,163,184,.9); font-size: 9px; font-weight: 900; white-space: nowrap; }
.prediction-line { min-width: 0; max-width: calc(100% - 26px); margin: 5px auto 0; color: rgba(148,163,184,.9); font-size: 10.5px; font-weight: 800; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.prediction-line strong { color: rgba(248,250,252,.9); font-weight: 900; }
.prediction-line.playoff-played { width: fit-content; max-width: calc(100% - 16px); margin-top: 7px; padding: 5px 9px; border: 1px solid rgba(117,170,219,.18); border-radius: 999px; color: rgba(226,232,240,.94); background: rgba(117,170,219,.065); font-size: 11px; box-shadow: 0 0 18px rgba(117,170,219,.05); }
.prediction-line.playoff-played strong { color: #bbf7d0; }
.match-foot { display: grid; grid-template-columns: minmax(0,1fr) auto minmax(0,1fr); align-items: center; gap: 7px; margin-top: 5px; min-height: 25px; }
.max-points { justify-self: start; color: rgba(187,247,208,.7); font-size: 9.5px; font-weight: 400; white-space: nowrap; }
.score-toggle-inline { justify-self: center; min-height: 23px; padding: 0 8px; border: 1px solid rgba(255,255,255,.075); border-radius: 999px; color: rgba(226,232,240,.82); background: rgba(255,255,255,.025); font-size: 10px; font-weight: 800; }
.score-toggle-inline.active { border-color: rgba(117,170,219,.18); background: rgba(117,170,219,.06); color: rgba(226,232,240,.94); }
.quick-action,
.quick-action.edit { justify-self: end; min-height: 25px; padding: 0 9px; border: 1px solid rgba(61,220,132,.2); border-radius: 999px; color: #bbf7d0; background: rgba(61,220,132,.08); font-size: 9.5px; font-weight: 900; }
.quick-action:hover,
.quick-action:focus-visible { border-color: rgba(61,220,132,.34); background: rgba(61,220,132,.13); outline: none; }
.result-points { justify-self: end; min-height: 25px; display: inline-flex; align-items: center; padding: 0 1px; color: rgba(203,213,225,.9); font-size: 10px; font-weight: 800; }
.result-points.positive { color: #bbf7d0; }
.result-points.negative { color: #fecaca; }

.match-detail, .group-detail { display: grid; gap: 8px; padding: 0 10px 10px; animation: reveal .18s ease-out; }
@keyframes reveal { from { opacity: .35; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.box { border: 1px solid rgba(255,255,255,.085); border-radius: 10px; background: rgba(0,0,0,.12); padding: 10px; }
.box.compact { padding: 9px; }
.box h3 { margin: 0 0 8px; color: var(--white); font-size: 12px; font-weight: 900; text-transform: uppercase; }
.prediction-panel summary { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.prediction-panel summary h3 { margin: 0; }
.prediction-panel summary span { min-width: 0; color: rgba(148,163,184,.9); font-size: 11px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.prediction-panel[open] summary { margin-bottom: 8px; }
.box p { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.35; }
.box-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; }
.box-grid span { display: block; color: var(--muted); font-size: 10px; font-weight: 900; text-transform: uppercase; }
.box-grid strong { display: block; margin-top: 2px; color: var(--white); font-size: 13px; line-height: 1.2; }

.prediction-quick { display: grid; grid-template-columns: minmax(0,1fr) auto; align-items: center; gap: 7px; }
.prediction-quick.playoff-form { grid-template-columns: 1fr; justify-items: center; gap: 9px; }
.pick-row { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 5px; }
.pick { min-height: 30px; padding: 0 6px; border: 1px solid var(--line); border-radius: 8px; color: var(--white); background: rgba(255,255,255,.045); font-size: 11px; font-weight: 900; }
.pick.selected { color: var(--ink); background: linear-gradient(135deg, #fde68a, var(--gold)); border-color: rgba(246,196,83,.46); }
.playoff-score-grid { display: grid; grid-template-columns: 92px auto 92px; gap: 8px; align-items: end; justify-content: center; width: 100%; }
.playoff-score-team { display: grid; gap: 5px; min-width: 0; }
.playoff-score-team label { color: var(--white); font-size: 11px; font-weight: 900; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.playoff-score-input { justify-self: center; width: 72px; min-height: 42px; border: 1px solid var(--line); border-radius: 8px; color: var(--white); background: rgba(255,255,255,.045); font: 900 17px/1 var(--heading); text-align: center; }
.playoff-score-sep { color: var(--gold); font: 900 18px/1 var(--heading); padding-bottom: 11px; }
.penalty-choice { display: grid; gap: 7px; grid-column: 1 / -1; justify-self: center; width: min(100%, 250px); padding: 7px; border: 1px solid rgba(246,196,83,.16); border-radius: 9px; background: rgba(246,196,83,.055); }
.penalty-choice strong { color: var(--gold); font-size: 10.5px; font-weight: 900; text-align: center; text-transform: uppercase; }
.penalty-row { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 6px; }
.penalty-pick { min-height: 38px; border: 1px solid var(--line); border-radius: 8px; color: var(--white); background: rgba(255,255,255,.045); font-size: 11px; font-weight: 900; }
.penalty-pick.selected { color: var(--ink); background: linear-gradient(135deg, #fde68a, var(--gold)); border-color: rgba(246,196,83,.46); }
.save { width: auto; min-height: 30px; padding: 0 11px; border: 1px solid rgba(61,220,132,.2); border-radius: 8px; color: #bbf7d0; background: rgba(61,220,132,.08); font-size: 11px; font-weight: 900; }
.prediction-quick.playoff-form .save { width: min(100%, 250px); min-height: 34px; justify-self: center; }
.save.pending { opacity: .48; color: rgba(203,213,225,.78); border-color: rgba(148,163,184,.12); background: rgba(148,163,184,.055); }
.scoring-toggle-wrapper { justify-self: center; width: fit-content; margin-left: auto; margin-right: auto; }
.scoring-toggle { width: fit-content; min-width: 220px; justify-self: center; margin-top: 2px; border: 1px solid rgba(117,170,219,.16); border-radius: 9px; background: rgba(117,170,219,.045); }
.scoring-table { display: grid; grid-template-columns: max-content 34px; column-gap: 10px; row-gap: 4px; justify-content: center; padding: 8px 12px; color: rgba(148,163,184,.82); font-size: 11px; }
.scoring-label { text-align: right; }
.scoring-value { color: rgba(248,250,252,.94); text-align: right; font-weight: 900; }
.score-list { display: grid; gap: 5px; }
.score-list div { display: flex; justify-content: space-between; gap: 10px; color: var(--muted); font-size: 12px; }
.score-list strong { color: var(--white); }
.points { color: var(--gold); font-weight: 900; }
.won { color: var(--green); font: 900 18px/1 var(--heading); }

.group-card .teams-list { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 6px; margin-top: 8px; color: var(--muted); font-size: 12px; font-weight: 800; }
select { width: 100%; min-height: 40px; border: 1px solid var(--line); border-radius: 9px; color: var(--white); background: #0d1725; padding: 0 10px; }

.phase-title { margin: 8px 0 0; color: var(--blue); font: 900 16px/1 var(--heading); text-transform: uppercase; }
.standings-table { display: grid; gap: 3px; }
.standing-row { display: grid; grid-template-columns: 24px minmax(82px,1fr) repeat(5, 34px); align-items: center; gap: 4px; min-height: 25px; color: rgba(226,232,240,.9); font-size: 11px; font-weight: 800; }
.standing-row.header { color: var(--muted); font-size: 9.5px; text-transform: uppercase; }
.standing-row .qualified { color: #bbf7d0; }
.standing-row span:nth-child(n+3) { text-align: right; }
.bracket-list { display: grid; gap: 8px; }
.evolution-match { border: 1px solid var(--line); border-radius: 11px; background: var(--surface); padding: 10px; }
.evolution-match small { display: block; margin-bottom: 6px; color: var(--muted); font-size: 10px; font-weight: 900; text-transform: uppercase; }
.evolution-match strong { display: block; color: var(--white); font-size: 13px; line-height: 1.25; }
.evolution-match span { display: block; margin-top: 3px; color: rgba(148,163,184,.88); font-size: 11px; font-weight: 800; }

.world-bracket {
    width: min(100%, calc(100vw - 28px));
    min-width: 0;
    max-width: calc(100vw - 28px);
    border: 1px solid rgba(255,255,255,.105);
    border-radius: 12px;
    background:
        radial-gradient(circle at 82% 12%, rgba(61,220,132,.08), transparent 30%),
        radial-gradient(circle at 6% 0%, rgba(117,170,219,.10), transparent 34%),
        rgba(255,255,255,.035);
    padding: 8px;
    overflow: hidden;
}
.world-bracket-tabs {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    width: 100%;
    min-width: 0;
    max-width: 100%;
    gap: 1px;
    padding-bottom: 8px;
    overflow: hidden;
}
.world-bracket-tab {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    min-height: 28px;
    padding: 0;
    border: 1px solid rgba(255,255,255,.16);
    border-radius: 6px;
    color: rgba(203,213,225,.86);
    background: rgba(255,255,255,.025);
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 9px;
    font-weight: 900;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
}
.world-bracket-tab.active {
    border-color: rgba(117,170,219,.46);
    color: #06111f;
    background: linear-gradient(135deg, #bbf7d0, #75aadb);
}
.world-zoom-controls {
    display: grid;
    grid-template-columns: 26px 36px 26px minmax(0, 1fr);
    gap: 2px;
    padding-bottom: 8px;
}
.world-zoom-btn,
.world-zoom-value {
    min-width: 0;
    min-height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255,255,255,.13);
    border-radius: 6px;
    color: rgba(226,232,240,.9);
    background: rgba(255,255,255,.035);
    font-size: 10px;
    font-weight: 900;
    white-space: nowrap;
    padding: 0 3px;
}
.world-zoom-btn.fit {
    color: #bbf7d0;
    border-color: rgba(61,220,132,.22);
    background: rgba(61,220,132,.07);
}
.world-zoom-value {
    color: rgba(148,163,184,.95);
    pointer-events: none;
}
.world-bracket-stage {
    position: relative;
    height: min(580px, calc(100vh - 260px));
    min-height: 430px;
    overflow: auto;
    border: 1px solid rgba(255,255,255,.09);
    border-radius: 9px;
    background: rgba(4,9,16,.66);
    -webkit-overflow-scrolling: touch;
}
.world-bracket-stage::-webkit-scrollbar { width: 4px; height: 4px; }
.world-bracket-stage::-webkit-scrollbar-thumb { background: rgba(117,170,219,.26); border-radius: 99px; }
.world-bracket-scale {
    position: relative;
    transform-origin: top left;
}
.world-bracket-inner {
    display: flex;
    align-items: flex-start;
    gap: 28px;
    width: max-content;
    min-width: 100%;
    min-height: 100%;
    padding: 12px 12px 20px;
}
.world-bracket-svg { display: none; }
.world-column {
    position: relative;
    flex: 0 0 160px;
    width: 160px;
}
.world-bracket-phase {
    margin-bottom: 10px;
    color: rgba(148,163,184,.9);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: .04em;
    text-align: center;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.world-bracket-phase.focus { color: #bbf7d0; }
.world-column-matches { position: relative; }
.world-card {
    position: absolute;
    width: 160px;
    height: 54px;
    display: grid;
    gap: 3px;
    align-content: center;
    overflow: visible;
    border: 1px solid rgba(255,255,255,.13);
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(255,255,255,.07), rgba(255,255,255,.025));
    box-shadow: 0 8px 20px rgba(0,0,0,.18);
    transition: border-color .18s, background .18s, box-shadow .18s, transform .18s;
    padding: 7px 25px 10px 8px;
}
.world-column:not(.phase-final) .world-card::after {
    display: none;
}
.world-card.focus {
    border-color: rgba(117,170,219,.42);
    background: linear-gradient(135deg, rgba(117,170,219,.16), rgba(255,255,255,.045));
}
.world-card.final {
    border-color: rgba(187,247,208,.42);
    background:
        radial-gradient(circle at 82% 10%, rgba(246,196,83,.18), transparent 34%),
        linear-gradient(135deg, rgba(61,220,132,.16), rgba(117,170,219,.10));
    box-shadow: 0 0 0 1px rgba(187,247,208,.08), 0 14px 34px rgba(61,220,132,.12);
}
.world-card-meta {
    position: absolute;
    top: 6px;
    right: 7px;
    color: rgba(246,196,83,.92);
    font-size: 8px;
    font-weight: 900;
    letter-spacing: .01em;
    text-transform: uppercase;
    white-space: nowrap;
}
.world-score { color: inherit; }
.world-score.pending { color: rgba(148,163,184,.82); }
.world-match-number {
    position: absolute;
    right: 6px;
    bottom: 4px;
    color: rgba(148,163,184,.54);
    font-size: 8px;
    font-weight: 900;
    line-height: 1;
    letter-spacing: .02em;
}
.world-team {
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr);
    align-items: center;
    gap: 4px;
    min-width: 0;
    color: rgba(226,232,240,.92);
    font-size: 10.5px;
    font-weight: 800;
    line-height: 1.08;
}
.world-team.real { color: rgba(248,250,252,.96); }
.world-team.placeholder {
    grid-template-columns: minmax(0, 1fr);
    color: rgba(148,163,184,.70);
    font-size: 9.5px;
    font-weight: 800;
}
.world-team.winner {
    color: #f8fafc;
    font-weight: 900;
    text-shadow: 0 0 12px rgba(187,247,208,.18);
}
.world-team.loser {
    color: rgba(148,163,184,.56);
    opacity: .68;
}
.world-flag {
    width: 16px;
    height: 12px;
    display: block;
    object-fit: cover;
    border: 1px solid rgba(255,255,255,.16);
    border-radius: 3px;
    box-shadow: 0 0 0 1px rgba(0,0,0,.10);
    filter: saturate(1.08);
}
.world-team.loser .world-flag {
    opacity: .58;
    filter: saturate(.7);
}
.flag-ARG { background: linear-gradient(#75aadb 0 33%, #fff 33% 66%, #75aadb 66%); }
.flag-MEX { background: linear-gradient(90deg, #15803d 0 33%, #fff 33% 66%, #b91c1c 66%); }
.flag-BRA { background: linear-gradient(135deg, #16a34a 0 42%, #facc15 42% 58%, #16a34a 58%); }
.flag-USA { background: linear-gradient(#b91c1c 0 14%, #fff 14% 28%, #b91c1c 28% 42%, #fff 42% 56%, #b91c1c 56% 70%, #fff 70% 84%, #b91c1c 84%); }
.flag-FRA { background: linear-gradient(90deg, #1d4ed8 0 33%, #fff 33% 66%, #dc2626 66%); }
.flag-SEN { background: linear-gradient(90deg, #15803d 0 33%, #facc15 33% 66%, #dc2626 66%); }
.flag-ESP { background: linear-gradient(#b91c1c 0 25%, #facc15 25% 75%, #b91c1c 75%); }
.flag-KOR { background: linear-gradient(#fff 0 100%); }
.flag-GER { background: linear-gradient(#111827 0 33%, #dc2626 33% 66%, #facc15 66%); }
.flag-MAR { background: #b91c1c; }
.flag-POR { background: linear-gradient(90deg, #15803d 0 42%, #dc2626 42%); }
.flag-KSA { background: #15803d; }
.flag-URU { background: linear-gradient(#fff 0 12%, #60a5fa 12% 24%, #fff 24% 36%, #60a5fa 36% 48%, #fff 48% 60%, #60a5fa 60% 72%, #fff 72% 84%, #60a5fa 84%); }
.flag-JPN { background: radial-gradient(circle, #dc2626 0 28%, transparent 30%), #fff; }
.flag-ENG { background: linear-gradient(90deg, transparent 0 42%, #dc2626 42% 58%, transparent 58%), linear-gradient(transparent 0 38%, #dc2626 38% 62%, transparent 62%), #fff; }
.flag-CAN { background: linear-gradient(90deg, #dc2626 0 28%, #fff 28% 72%, #dc2626 72%); }
.world-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.world-card.final .world-team { font-size: 11px; }
.world-card.final .world-card-meta { font-size: 9px; }
.world-line {
    fill: none;
    stroke: rgba(148,163,184,.18);
    stroke-width: .65;
    stroke-linecap: round;
    stroke-linejoin: round;
}
.world-line.live {
    stroke: rgba(117,170,219,.48);
    stroke-width: .9;
    filter: drop-shadow(0 0 2px rgba(61,220,132,.22));
}

@media (max-width: 370px) {
    .screen { padding-left: 10px; padding-right: 10px; }
    .tab { font-size: 11px; }
    .teams { grid-template-columns: minmax(58px,1fr) 27px 62px 27px minmax(58px,1fr); column-gap: 4px; }
    .scoring-toggle { width: fit-content; min-width: 0; max-width: 100%; }
    .score-toggle-inline { min-height: 22px; padding: 0 7px; font-size: 9.5px; }
    .prediction-quick { grid-template-columns: 1fr; }
    .save { justify-self: end; }
}

/* H3 fix-3: estrategia distinta a fix-1/fix-2. Conservamos nombres LARGOS
   (16avos / Octavos / Cuartos / Semis / Final) en todos los anchos.
   Para que entren en mobile:
   - reducimos el padding lateral de .screen (Shadow DOM, no afecta resto
     de la app — confirmado: el CSS de Prode vive en PRODE_CSS dentro del
     shadow montado por renderProde),
   - reducimos padding y border-radius de .world-bracket y sus tabs,
   - bajamos font-size + letter-spacing a valores apretados pero leíbles,
   - píldoras del header con flex-wrap para que rompan en 2 líneas si
     hace falta. */
@media (max-width: 480px) {
    .screen { padding-left: 4px; padding-right: 4px; }
    .chips { flex-wrap: wrap; padding: 8px 0 2px; gap: 5px; }
    .chip { font-size: 10px; padding: 0 7px; min-height: 24px; }
    .world-bracket { padding: 4px; }
    .world-bracket-tabs { gap: 2px; padding-bottom: 8px; }
    .world-bracket-tab {
        min-height: 28px;
        padding: 0 2px;
        border-radius: 6px;
        font-size: 9px;
        letter-spacing: -0.1px;
    }
    .world-zoom-controls {
        grid-template-columns: 24px 36px 24px minmax(0, 1fr);
        gap: 2px;
        padding-bottom: 8px;
    }
    .world-zoom-btn, .world-zoom-value {
        min-height: 26px;
        font-size: 10px;
        padding: 0 3px;
    }
}
/* H3 fix-3: extra apretado para 320px (iPhone SE chico, Galaxy A pequeños). */
@media (max-width: 360px) {
    .world-bracket-tab { font-size: 8.5px; padding: 0 1px; }
}

/* H4 migración: CSS del componente compartido playoff-prediction-card.
   Inyectado dentro del Shadow DOM (las variables CSS --celeste, --white,
   --font-heading existen también acá por herencia). */
${PLAYOFF_CARD_CSS}
`

// ─── HTML del sandbox (verbatim) ────────────────────────────────────────────

const PRODE_HTML = `
    <main class="screen">
        <div class="prode-screen-title">Partido a Partido + Tus Predicciones</div>

        <nav class="tabs" aria-label="Secciones principales">
            <button class="tab info active" type="button" data-tab="evolution">Evolución Mundial</button>
            <button class="tab" type="button" data-tab="groups">PRODE Grupos</button>
            <button class="tab" type="button" data-tab="playoffs">PRODE Playoffs</button>
        </nav>

        <section id="panel-evolution" class="panel active">
            <div class="subtabs info" aria-label="Evolucion del Mundial">
                <button class="subtab active" type="button" data-subtab="evolution-groups">Posiciones grupos</button>
                <button class="subtab" type="button" data-subtab="evolution-bracket">Llave</button>
            </div>
            <div id="subpanel-evolution-groups" class="subpanel active"></div>
            <div id="subpanel-evolution-bracket" class="subpanel">
                <div id="contenedor-llave" class="vista-llave">
                    <!-- renderWorldBracket() puebla este contenedor -->
                </div>
            </div>
        </section>

        <section id="panel-groups" class="panel">
            <div class="subtabs" aria-label="Orden de partidos">
                <button class="subtab active" type="button" data-subtab="date">Por fecha</button>
                <button class="subtab" type="button" data-subtab="group">Por grupo</button>
            </div>
            <div id="subpanel-date" class="subpanel active"></div>
            <div id="subpanel-group" class="subpanel"></div>
        </section>

        <section id="panel-playoffs" class="panel">
            <div class="subtabs playoff-tabs" aria-label="Fases de PRODE Playoffs">
                <button class="subtab active" type="button" data-subtab="playoff-16avos">16avos</button>
                <button class="subtab" type="button" data-subtab="playoff-octavos">Octavos</button>
                <button class="subtab" type="button" data-subtab="playoff-cuartos">Cuartos</button>
                <button class="subtab" type="button" data-subtab="playoff-semifinal">Semifinales</button>
                <button class="subtab" type="button" data-subtab="playoff-final">Final</button>
            </div>
            <div id="subpanel-playoff-16avos" class="subpanel active"></div>
            <div id="subpanel-playoff-octavos" class="subpanel"></div>
            <div id="subpanel-playoff-cuartos" class="subpanel"></div>
            <div id="subpanel-playoff-semifinal" class="subpanel"></div>
            <div id="subpanel-playoff-final" class="subpanel"></div>
        </section>
    </main>
`

// ─── Constantes del sandbox (verbatim) ──────────────────────────────────────

const fmt = new Intl.NumberFormat('es-UY')

const statusLabels = {
    scheduled: 'PROGRAMADO',
    live: 'EN VIVO',
    finished: 'FINALIZADO'
}

// Multiplicadores de fase — canónico Prode_Scoring v1.3 §13 (16avos x1 · octavos
// x2 · cuartos x4 · semifinales x8 · final x12). Antes semifinal/final estaban
// x6/x10 (bug: divergían de PHASE_MULT en playoff-prediction-card.js, que sí es
// canónico). Solo impacta los displays de playoff SIN equipos (getPuntosEnJuego
// / getResumenScoring del path genérico); los partidos definidos usan las del
// componente. Alineado para que el desglose pre-definición coincida con el real.
const phaseMeta = {
    '16avos': { label: '16avos', multiplier: 1 },
    octavos: { label: 'Octavos', multiplier: 2 },
    cuartos: { label: 'Cuartos', multiplier: 4 },
    semifinal: { label: 'Semifinal', multiplier: 8 },
    final: { label: 'Final', multiplier: 12 }
}

// Estado de la pantalla (una sola instancia viva por vez; mountProdeApp lo
// repuebla en cada entrada a /prode). Las funciones de render del sandbox
// leían variables de módulo; acá leen de S — mismo patrón, sin globals window.
const S = {
    root: null,
    playerId: null,
    groupMatches: [],
    playoffMatches: [],
    groups: [],
    standings: {},
    flags: {},
    names: {},
    predictions: {},
    collapsed: new Set(),
    openScoring: new Set(),
    bracketMatches: [],
    bracketRendered: false,
}

// ─── Carga de datos reales (reemplaza los mocks del sandbox) ────────────────

const loadProdeData = async () => {
    const [todos, gruposMundial, posiciones, equipos] = await Promise.all([
        fetchPartidos(),
        fetchGruposMundial(),
        fetchPosicionesGrupo(),
        fetchEquipos(),
    ])
    const groupMatches = todos.filter((p) => p.fase === 'grupo')
    // Tercer puesto: persiste en DB para sync API-Football pero NO se predice
    // ni se muestra en UI. Filtrarlo acá lo oculta de la solapa Final del
    // Prode, del bracket visual y de cualquier consumer de S.playoffMatches.
    const playoffMatches = todos.filter((p) => p.fase !== 'grupo' && !p.es_tercer_puesto)
    const groups = gruposMundial.map((g) => ({ id: g.grupo, teams: g.equipos.map((e) => e.codigo) }))
    const flags = {}
    const names = {}
    for (const e of equipos) {
        flags[e.codigo] = e.bandera_url
        names[e.codigo] = e.nombre
    }
    // standings: usa posiciones_grupo si tiene filas; si está vacía (hoy, pre
    // torneo) arma una baseline en cero con los 4 equipos del grupo.
    const standings = {}
    for (const g of groups) {
        const filas = posiciones
            .filter((p) => p.grupo_mundial === g.id)
            .sort((a, b) => a.posicion - b.posicion)
        standings[g.id] = filas.length
            ? filas.map((p) => ({
                code: p.equipo_codigo,
                pj: p.jugados,
                gf: p.goles_favor,
                gc: p.goles_contra,
                pts: p.puntos,
                q: p.posicion <= 2,
            }))
            : g.teams.map((code) => ({ code, pj: 0, gf: 0, gc: 0, pts: 0, q: false }))
    }
    return { groupMatches, playoffMatches, groups, standings, flags, names, total: todos.length }
}

// ─── Entry-point ────────────────────────────────────────────────────────────

export const renderProde = async (codigo, subRoute) => {
    const container = appRoot || document.getElementById('app-root')
    if (!container) return
    // Encabezado común (mismo que Mis Grupos / Ranking / Perfil) en light DOM,
    // arriba del host del sandbox. Se pinta ya en el skeleton para que no salte
    // cuando llegan los datos.
    container.innerHTML = renderActiveGroupBar(codigo) + buildProdeSkeleton()

    // Jugador del usuario actual en este grupo: define de quién son las
    // predicciones (el Prode es por jugador, es decir por grupo). Sin
    // membership el Prode se muestra igual, pero no se puede guardar.
    const membership = getMembership(codigo)
    const playerId = membership?.player_id || membership?.id || null

    let data
    let predicciones = []
    try {
        // DT-H3-resto: tope global a la carga del Mundial. Si timeoutea, el catch
        // de abajo pinta la card de error+Reintentar (ya existente).
        const loaded = await withTimeout(Promise.all([
            loadProdeData(),
            playerId ? getPredicciones(playerId) : Promise.resolve([]),
        ]), NET_TIMEOUT_MS, 'prode-load')
        data = loaded[0]
        predicciones = loaded[1]
    } catch (err) {
        console.error('[prode] no se pudo cargar el Mundial:', err)
        if (!window.location.hash.startsWith('#/prode/')) return
        container.innerHTML = `
            <div class="glass-card view-enter" style="text-align:center;">
                <p class="subtitle mb-4">No pudimos cargar el Mundial. ${err.message}</p>
                <button class="btn btn-secondary" type="button" onclick="window.location.reload()">Reintentar</button>
            </div>${renderBottomNav(codigo, 'prode')}`
        return
    }

    // Si el usuario navegó a otra pantalla mientras se cargaban los datos, no
    // pisamos lo que haya pintado el router.
    if (!window.location.hash.startsWith('#/prode/')) return

    container.innerHTML = `${renderActiveGroupBar(codigo)}<div id="prode-host" style="width:100%;"></div>${renderBottomNav(codigo, 'prode')}`
    const host = container.querySelector('#prode-host')
    // Shadow DOM: el CSS del sandbox queda 100% aislado del resto de la SPA.
    const shadow = host.attachShadow({ mode: 'open' })
    shadow.innerHTML = `<style>${PRODE_CSS}</style>${PRODE_HTML}`
    mountProdeApp(shadow, data, subRoute, playerId, predicciones)
}

const mountProdeApp = (root, data, subRoute, playerId, predicciones) => {
    S.root = root
    S.playerId = playerId || null
    S.groupMatches = data.groupMatches
    S.playoffMatches = data.playoffMatches
    S.groups = data.groups
    S.standings = data.standings
    S.flags = data.flags
    S.names = data.names
    // F1.3.c2 — siembra las predicciones ya guardadas (cierra DT-R).
    S.predictions = seedPredictions(predicciones, [...data.groupMatches, ...data.playoffMatches])
    S.collapsed = new Set(Object.keys(S.predictions))
    S.openScoring = new Set()
    S.bracketRendered = false
    S.bracketMatches = buildBracketMatches(data.playoffMatches)

    // Chips del hero con los conteos reales de la base.
    const chips = root.querySelectorAll('.chips .chip strong')
    if (chips[0]) chips[0].textContent = data.total
    if (chips[1]) chips[1].textContent = data.groupMatches.length
    if (chips[2]) chips[2].textContent = data.playoffMatches.length

    renderAll()
    bindEvents()
    applySubRoute(subRoute)
}

function applySubRoute(subRoute) {
    const map = { grupos: 'groups', groups: 'groups', playoffs: 'playoffs', evolution: 'evolution' }
    const target = map[subRoute]
    if (!target) return
    const btn = S.root.querySelector(`[data-tab="${target}"]`)
    if (btn) btn.click()
}

// Adapta los partidos de eliminatoria reales al shape que consume el bracket
// visual. Hoy los 32 cruces no tienen equipos definidos → placeholders.
//
// H3 (T3 + T4):
//   T4: omitimos el partido por el tercer puesto. DT-M sigue abierta
//       (`es_tercer_puesto = false` en los 104 partidos del fixture); como
//       fallback robusto, dentro de los partidos con fase='final' tomamos
//       el de `match_number` más alto como la final real (104) y excluimos
//       el resto (típicamente 103 = tercer puesto).
//   T3: derivamos `from = [n_prev_a, n_prev_b]` por pares consecutivos
//       dentro de la fase anterior (ordenadas por match_number). Esto
//       activa `assignBracketPositions`, que centra cada partido de fase N
//       entre los dos de fase N-1 que lo alimentan (estructura de bracket).
function buildBracketMatches(playoffMatches) {
    const PHASE_ORDER = ['16avos', 'octavos', 'cuartos', 'semifinal', 'final']

    // T4: detectar tercer puesto (DT-M fallback)
    const finales = playoffMatches.filter((p) => p.fase === 'final')
    const finalRealN = finales.length
        ? Math.max(...finales.map((p) => p.match_number))
        : null
    const incluir = (p) =>
        !p.es_tercer_puesto &&
        (p.fase !== 'final' || p.match_number === finalRealN)

    const filtrados = playoffMatches.filter(incluir)

    // OPCIÓN A: el array se ordena en "ORDEN DE BRACKET" derivado del árbol de cableado
    // real (viene_local_de / viene_visitante_de), NO por match_number. Así la derivación
    // posicional de `from` (más abajo) y el posicionamiento por índice del render quedan
    // alineados con el recorrido FIFA SIN tocar posiciones, líneas, `from` ni el render:
    // sólo cambia QUÉ partido cae en cada casilla. Tokens 'Mxx' = ganador del partido xx;
    // los de 16avos son de grupo (sin feeders) → hojas del árbol.
    const tokenN = (t) => { const m = /^M(\d+)$/.exec(String(t || '').trim()); return m ? Number(m[1]) : null }
    const feeders = {}
    for (const p of filtrados) {
        const a = tokenN(p.viene_local_de), b = tokenN(p.viene_visitante_de)
        if (a != null && b != null) feeders[p.match_number] = [a, b]
    }
    // Recorro desde la final (local → arriba, visitante → abajo) numerando las hojas
    // top→bottom; cada nodo hereda el índice de su hoja superior. Ese índice = clave de orden.
    let leafSeq = 0
    const orderKey = {}
    const walk = (n) => {
        if (orderKey[n] != null) return orderKey[n]
        const f = feeders[n]
        if (!f) { orderKey[n] = leafSeq++; return orderKey[n] }
        const start = walk(f[0]); walk(f[1]); orderKey[n] = start; return start
    }
    if (finalRealN != null) walk(finalRealN)
    const bracketKey = (p) => orderKey[p.match_number] ?? (1e6 + p.match_number)
    const phaseRank = (p) => PHASE_ORDER.indexOf(p.fase)
    const sorted = [...filtrados].sort((a, b) => phaseRank(a) - phaseRank(b) || bracketKey(a) - bracketKey(b))

    // T3: agrupar por fase y derivar `from` por pares consecutivos
    const byPhase = {}
    for (const fase of PHASE_ORDER) {
        byPhase[fase] = sorted.filter((p) => p.fase === fase).map((p) => p.match_number)
    }
    const fromByN = {}
    for (let i = 1; i < PHASE_ORDER.length; i++) {
        const prev = byPhase[PHASE_ORDER[i - 1]] || []
        const curr = byPhase[PHASE_ORDER[i]] || []
        curr.forEach((n, k) => {
            const a = prev[2 * k]
            const b = prev[2 * k + 1]
            if (a != null && b != null) fromByN[n] = [a, b]
        })
    }

    return sorted.map((p) => ({
        n: p.match_number,
        fase: p.fase,
        home: p.equipo_local_codigo
            ? { type: 'team', code: p.equipo_local_codigo, name: p.equipo_local_nombre || p.equipo_local_codigo, bandera_url: p.equipo_local_bandera_url || null }
            : { type: 'placeholder', name: p.viene_local_de || 'A definir' },
        away: p.equipo_visitante_codigo
            ? { type: 'team', code: p.equipo_visitante_codigo, name: p.equipo_visitante_nombre || p.equipo_visitante_codigo, bandera_url: p.equipo_visitante_bandera_url || null }
            : { type: 'placeholder', name: p.viene_visitante_de || 'A definir' },
        score: p.goles_local != null && p.goles_visitante != null ? `${p.goles_local}-${p.goles_visitante}` : null,
        from: fromByN[p.match_number],
    }))
}

// ─── Funciones del sandbox (verbatim; document → S.root, mocks → S.*) ───────

function puedePredecir(partido) {
    return partido.equipo_local_codigo !== null &&
        partido.equipo_visitante_codigo !== null
}

function getEstadoRealPartido(partido) {
    if (partido.status === 'live') return 'live'
    if (partido.status === 'finished') return 'finished'
    return 'scheduled'
}

function getCtaDisponible(partido, prediccion = S.predictions[partido.partido_id]) {
    if (!puedePredecir(partido)) return null
    if (getEstadoRealPartido(partido) !== 'scheduled') return null
    return prediccion?.pick ? 'EDITAR' : 'JUGAR'
}

function juegaArgentina(partido) {
    return partido.argentina ||
        partido.equipo_local_codigo === 'ARG' ||
        partido.equipo_visitante_codigo === 'ARG'
}

function getPuntosEnJuego(partido) {
    if (partido.fase === 'grupo') return 5
    const base = 80
    const mult = phaseMeta[partido.fase]?.multiplier || 1
    return base * mult * (juegaArgentina(partido) ? 3 : 1)
}

function getResumenScoring(partido) {
    if (partido.fase === 'grupo') {
        return [
            ['Acierto ganador', '+5'],
            ['Empate acertado', '+1'],
            ['Error leve', '0'],
            ['Error contrario', '-1'],
        ]
    }
    const lineas = [
        ['Ganador', '+10'],
        ['Diferencia', '+10'],
        ['Goles del ganador', '+10'],
        ['Acierto completo', '+50'],
        ['Multiplicador de fase', `x${phaseMeta[partido.fase]?.multiplier || 1}`],
    ]
    if (juegaArgentina(partido)) {
        // Equipos definidos y ARG juega: multiplicador firme (igual que hoy).
        lineas.push(['Multiplicador Argentina', 'x3'])
    } else if (!puedePredecir(partido)) {
        // Playoff sin equipos definidos: todavía no se sabe si jugará Argentina,
        // pero el desglose base se muestra igual + esta línea corta avisando del
        // multiplicador. Canónico Prode_Scoring v1.3 §14 (ARG x3 en playoffs).
        lineas.push(['⚡ Si juega Argentina', 'x3'])
    }
    return lineas
}

function contextLabel(partido) {
    if (partido.fase === 'grupo') return `Grupo ${partido.grupo_mundial}`
    return phaseMeta[partido.fase]?.label || partido.fase
}

function resultOrTime(partido) {
    if (partido.goles_local !== null && partido.goles_visitante !== null) return `${partido.goles_local} - ${partido.goles_visitante}`
    if (getEstadoRealPartido(partido) === 'scheduled') return '- -'
    return 'EN VIVO'
}

function scoreClass(partido) {
    if (getEstadoRealPartido(partido) === 'scheduled') return 'score'
    return partido.goles_local !== null ? 'score' : 'time'
}

function penaltiesLine(partido) {
    // Solo cuando hay penales REALES (definición por penales en playoff). Los partidos de
    // grupo tienen penales_local/penales_visitante = null → no se muestra la línea (antes
    // el guard chequeaba `undefined` y dejaba pasar el null → "Penales: null - null").
    if (partido.penales_local == null || partido.penales_visitante == null) return ''
    return `<span class="penalties">Penales: ${partido.penales_local} - ${partido.penales_visitante}</span>`
}

function matchPriority(partido) {
    const state = getEstadoRealPartido(partido)
    if (state === 'live') return 0
    if (state === 'scheduled') return 1
    return 2
}

function sortRelevantMatches(list) {
    const now = Date.now()
    return [...list].sort((a, b) => {
        const priorityDiff = matchPriority(a) - matchPriority(b)
        if (priorityDiff) return priorityDiff
        const aTime = new Date(a.fecha_hora).getTime()
        const bTime = new Date(b.fecha_hora).getTime()
        if (getEstadoRealPartido(a) === 'scheduled') {
            return Math.abs(aTime - now) - Math.abs(bTime - now)
        }
        return aTime - bTime
    })
}

function findMatch(id) {
    return [...S.groupMatches, ...S.playoffMatches].find((partido) => partido.partido_id === id)
}

function refreshMatchCardElement(card, keepOpen = true) {
    const id = card?.dataset?.card
    const partido = findMatch(id)
    if (!partido || !card) return
    const wrapper = document.createElement('div')
    wrapper.innerHTML = renderMatchCard(partido).trim()
    const next = wrapper.firstElementChild
    if (keepOpen) next.setAttribute('open', '')
    card.replaceWith(next)
    // H4 migración: re-wirear si el reemplazado es una card de playoff
    // (el componente compartido no usa el wiring de bindEvents).
    if (partido.fase !== 'grupo') wireProdePlayoffCard(next, partido)
}

// H4 migración: wirea una card de playoff del componente compartido.
// Llamado tras renderAll (cada subpanel playoff) y tras refreshMatchCardElement.
function wireProdePlayoffCard(wrap, partido) {
    if (!wrap || !wrap.matches?.('.ppc-wrap')) return
    const pred = S.predictions[partido.partido_id]
    const predInicial = pred?.goalsHome !== undefined && pred?.goalsHome !== ''
        ? {
            goalsHome: pred.goalsHome ?? '',
            goalsAway: pred.goalsAway ?? '',
            clasificado_codigo: pred.penaltyWinner === 'home' ? partido.equipo_local_codigo
                : pred.penaltyWinner === 'away' ? partido.equipo_visitante_codigo : null,
        }
        : null
    wirePlayoffCard(wrap, partido, predInicial, {
        onSave: async (payload) => {
            if (!S.playerId) return { ok: false, message: 'No te identificamos en el grupo.' }
            const { error } = await upsertPrediccion(S.playerId, payload.partido_id, payload)
            if (error) {
                console.error('[prode] upsertPrediccion playoff:', error)
                return { ok: false, message: traducirErrorPrediccion(error) }
            }
            // Sincronizar S.predictions[id] con el shape canónico de prode.js.
            const penaltyWinner = payload.clasificado_codigo === partido.equipo_local_codigo ? 'home'
                : payload.clasificado_codigo === partido.equipo_visitante_codigo ? 'away' : undefined
            const pickPlayoff = payload.score_local > payload.score_visitante ? 'home'
                : payload.score_local < payload.score_visitante ? 'away'
                : penaltyWinner
            const next = {
                goalsHome: payload.score_local,
                goalsAway: payload.score_visitante,
                pick: pickPlayoff,
                points: S.predictions[payload.partido_id]?.points || 0,
            }
            if (penaltyWinner) next.penaltyWinner = penaltyWinner
            next.label = playoffPredictionLabel(partido, next)
            S.predictions[payload.partido_id] = next
            return { ok: true }
        },
    })
}

function wireAllProdePlayoffCards() {
    if (!S.root) return
    S.root.querySelectorAll('.ppc-wrap[data-card]').forEach((wrap) => {
        const id = wrap.getAttribute('data-card')
        const partido = findMatch(id)
        if (partido && partido.fase !== 'grupo') wireProdePlayoffCard(wrap, partido)
    })
}

function refreshOtherMatchCards(id, exceptCard) {
    S.root.querySelectorAll(`[data-card="${id}"]`).forEach((card) => {
        if (card !== exceptCard) refreshMatchCardElement(card, card.open)
    })
}

// DT-BL: día + mes corto en ART para la meta line de cada card de partido.
// Se construye con Intl.DateTimeFormat + timeZone ART (en vez de getDate()
// del navegador) para que el día no se desplace por la TZ del runtime.
const matchCardFechaFmt = new Intl.DateTimeFormat('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: 'short' })

function formatKickoff(iso) {
    return formatHoraART(iso)
}

function formatMatchDateTime(iso) {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const parts = matchCardFechaFmt.formatToParts(d)
    const day = parts.find(p => p.type === 'day')?.value || ''
    const month = (parts.find(p => p.type === 'month')?.value || '').replace('.', '').toUpperCase()
    return `${day} ${month} &middot; <strong>${formatHoraART(iso)}</strong>`
}

function matchMetaLine(partido) {
    return `${contextLabel(partido)} &middot; ${formatMatchDateTime(partido.fecha_hora)}`
}

function teamNameClass(name, side) {
    const length = (name || '').length
    let sizeClass = ''
    if (length > 12) sizeClass = 'team-name-long'
    if (length > 16) sizeClass = 'team-name-extra-long'
    return ['team-name', side, sizeClass].filter(Boolean).join(' ')
}

function dateKey(partido) {
    return fechaKeyART(partido.fecha_hora)
}

function dateTitle(key) {
    // `key` es YYYY-MM-DD en ART; T15:00:00Z = 12:00 ART → mismo día calendario.
    return formatFechaLargaART(`${key}T15:00:00Z`)
}

function renderFlag(url, code) {
    return url ? `<img class="flag" src="${url}" alt="">` : `<span class="flag-code">${code || 'TBD'}</span>`
}

function renderMiniFlag(code) {
    return `<img class="mini-flag" src="${S.flags[code] || ''}" alt="${code}">`
}

function statusBlock(partido) {
    const state = getEstadoRealPartido(partido)
    const { subestado, minuto } = lineasEstadoVivo(partido)
    return `
        <div class="match-status ${state === 'live' ? 'live' : ''}">
            <span>${statusLabels[state]}</span>
            ${state === 'live' ? `<span class="status-detail">${subestado}</span>` : ''}
            ${state === 'live' && minuto ? `<span class="status-detail">${minuto}</span>` : ''}
        </div>
    `
}

function predictionLine(partido, pred) {
    if (pred?.label) return `${partido.fase === 'grupo' ? 'Jugada' : 'Jugaste'}: <strong>${pred.label}</strong>`
    if (!puedePredecir(partido)) return 'Se habilita cuando esten definidos los equipos'
    if (getEstadoRealPartido(partido) === 'scheduled') return 'Sin jugada todavia'
    return 'No jugaste este partido'
}

function predictionLineClass(partido, pred) {
    const playedPlayoff = partido.fase !== 'grupo' && pred?.label
    return `prediction-line ${playedPlayoff ? 'playoff-played' : ''}`
}

function playoffPredictionReady(pred) {
    if (pred?.goalsHome === undefined || pred?.goalsAway === undefined) return false
    if (pred.goalsHome === '' || pred.goalsAway === '') return false
    if (Number(pred.goalsHome) === Number(pred.goalsAway) && !pred.penaltyWinner) return false
    return true
}

function playoffPredictionLabel(partido, pred) {
    if (!playoffPredictionReady(pred)) return ''
    const homeGoals = Number(pred.goalsHome)
    const awayGoals = Number(pred.goalsAway)
    const score = `${partido.equipo_local_nombre} ${homeGoals} - ${awayGoals} ${partido.equipo_visitante_nombre}`
    if (homeGoals !== awayGoals) return score
    const winnerName = pred.penaltyWinner === 'home' ? partido.equipo_local_nombre : partido.equipo_visitante_nombre
    return `${score} - Pasa ${winnerName} por penales`
}

function pointsClass(points) {
    if (points > 0) return 'positive'
    if (points < 0) return 'negative'
    return 'neutral'
}

function pointsLabel(points) {
    if (points > 0) return `+${fmt.format(points)} pts`
    return `${fmt.format(points)} pts`
}

function personalMatchSlot(partido, pred, cta) {
    const state = getEstadoRealPartido(partido)
    if (state === 'scheduled' && cta) {
        return `<button class="quick-action ${cta === 'EDITAR' ? 'edit' : ''}" type="button" data-open="${partido.partido_id}">${cta}</button>`
    }
    if (state === 'finished') {
        const points = pred?.points || 0
        return `<span class="result-points ${pointsClass(points)}">${pointsLabel(points)}</span>`
    }
    return ''
}

function scoreToggleButton(partido) {
    // 27-may noche (observación dueño): el botón "Ver puntos en juego" se
    // mantiene presente también en FINALIZADO. Muestra la grilla canónica
    // de referencia (+5/+1/0/-1 grupos · 80 base + multi + bonus playoffs),
    // sin lógica condicional según el caso real del jugador. Además su
    // presencia ocupa la col 2 de match-foot grid, dejando result-points
    // (col 3, justify-self: end) alineado a la derecha como corresponde.
    const isOpen = S.openScoring.has(partido.partido_id)
    return `<button class="score-toggle-inline ${isOpen ? 'active' : ''}" type="button" data-score="${partido.partido_id}">Ver puntos en juego</button>`
}

// renderMatchCard(partido, predOverride?)
//
// Si se pasa predOverride, lo usa en vez de S.predictions[partido.partido_id].
// Útil para reusar la card en otros módulos (perfil-prode) sin necesidad de
// pre-popular el estado interno de prode.
export function renderMatchCard(partido, predOverride) {
    // H4 migración: para partidos de playoff, la card entera la pinta el
    // componente compartido (src/ui/playoff-prediction-card.js) en formato
    // PRODE. Sin `<details>` expandible — los inputs ya viven inline en la
    // fila. El wiring se hace en renderAll() y refreshMatchCardElement().
    // Playoff CON equipos definidos: card de predicción completa (sin cambios).
    // Playoff SIN equipos (puedePredecir=false): cae al render genérico "A definir / locked"
    // de abajo —mismo look y texto que un partido de grupo bloqueado— en vez de
    // renderPlayoffCardProde (que mostraría "null" y dejaría predecir un partido vacío).
    if (partido.fase !== 'grupo' && puedePredecir(partido)) {
        const pred = (predOverride && typeof predOverride === 'object') ? predOverride : S.predictions[partido.partido_id]
        const predInicial = pred?.goalsHome !== undefined || pred?.goalsAway !== undefined
            ? { goalsHome: pred.goalsHome ?? '', goalsAway: pred.goalsAway ?? '', clasificado_codigo: pred.penaltyWinner === 'home' ? partido.equipo_local_codigo : pred.penaltyWinner === 'away' ? partido.equipo_visitante_codigo : null, puntos: pred.points ?? 0 }
            : null
        return renderPlayoffCardProde(partido, predInicial, S.openScoring.has(partido.partido_id))
    }
    const pred = (predOverride && typeof predOverride === 'object') ? predOverride : S.predictions[partido.partido_id]
    const cta = getCtaDisponible(partido, pred)
    const locked = !puedePredecir(partido)
    return `
        <details class="match ${partido.fase !== 'grupo' ? 'playoff' : ''} ${locked ? 'locked' : ''}" data-card="${partido.partido_id}">
            <summary>
                <div class="match-head">
                    <div class="match-topline">
                        <div class="match-meta">${matchMetaLine(partido)}</div>
                        ${statusBlock(partido)}
                    </div>
                    <div class="teams">
                        <span class="${teamNameClass(partido.equipo_local_nombre, 'home')}">${partido.equipo_local_nombre || 'A definir'}</span>
                        ${renderFlag(partido.equipo_local_bandera_url, partido.equipo_local_codigo)}
                        <div class="score-wrap">
                            <span class="${scoreClass(partido)}">${resultOrTime(partido)}</span>
                            ${penaltiesLine(partido)}
                        </div>
                        ${renderFlag(partido.equipo_visitante_bandera_url, partido.equipo_visitante_codigo)}
                        <span class="${teamNameClass(partido.equipo_visitante_nombre, 'away')}">${partido.equipo_visitante_nombre || 'A definir'}</span>
                    </div>
                    <div class="${predictionLineClass(partido, pred)}">${predictionLine(partido, pred)}</div>
                    <div class="match-foot">
                        <span class="max-points">Hasta ${fmt.format(getPuntosEnJuego(partido))} pts</span>
                        ${scoreToggleButton(partido)}
                        ${personalMatchSlot(partido, pred, cta)}
                    </div>
                </div>
            </summary>
            ${renderMatchExpanded(partido)}
        </details>
    `
}

function renderMatchExpanded(partido) {
    const state = getEstadoRealPartido(partido)
    const pred = S.predictions[partido.partido_id]
    if (!puedePredecir(partido)) {
        // Playoff sin equipos: el desplegable "Ver puntos en juego" muestra SOLO
        // el desglose base de la ronda (mismo componente scoring-toggle que grupos
        // y que los playoffs definidos) + la línea corta del multiplicador
        // Argentina. Ya NO va el cartel "A definir / Se habilita..." — era
        // redundante una vez que el desglose sí se muestra.
        if (partido.fase !== 'grupo') {
            const isOpen = S.openScoring.has(partido.partido_id)
            return `
                <div class="match-detail">
                    <div class="scoring-toggle-wrapper" data-scoring-wrapper="${partido.partido_id}"${isOpen ? '' : ' style="display:none;"'}>${renderScoringToggle(partido)}</div>
                </div>
            `
        }
        // Caso defensivo no-playoff sin equipos (los grupos siempre tienen
        // equipos; este path no debería darse): mantiene el cartel de hoy.
        return `<div class="match-detail"><div class="box compact"><h3>A definir</h3><p>Se habilita cuando esten definidos los clasificados.</p></div></div>`
    }
    const isEditing = state === 'scheduled' && !S.collapsed.has(partido.partido_id)
    const isOpen = S.openScoring.has(partido.partido_id)
    // El wrapper se renderiza siempre y se oculta con display:none cuando
    // no está abierto. Permite toggle CSS-puro desde el wiring del perfil
    // (que reusa la card en Shadow DOM sin acceso al bindEvents del Prode).
    return `
        <div class="match-detail">
            ${isEditing ? renderPredictionForm(partido, pred) : ''}
            <div class="scoring-toggle-wrapper" data-scoring-wrapper="${partido.partido_id}"${isOpen ? '' : ' style="display:none;"'}>${renderScoringToggle(partido)}</div>
        </div>
    `
}

function renderScoringToggle(partido) {
    return `
        <div class="scoring-toggle">
            <div class="scoring-table">
                ${getResumenScoring(partido).map(([label, pts]) => `<span class="scoring-label">${label}</span><span class="scoring-value">${pts}</span>`).join('')}
            </div>
        </div>
    `
}

function renderPredictionForm(partido, pred) {
    const isCollapsed = S.collapsed.has(partido.partido_id)
    const openAttr = isCollapsed ? '' : ' open'
    if (partido.fase !== 'grupo') {
        const homeGoals = pred?.goalsHome ?? ''
        const awayGoals = pred?.goalsAway ?? ''
        const isTie = homeGoals !== '' && awayGoals !== '' && Number(homeGoals) === Number(awayGoals)
        const ready = playoffPredictionReady(pred)
        return `
            <details class="box compact prediction-panel"${openAttr}>
                <summary><h3>Mi prediccion</h3>${isCollapsed ? `<span>${predictionLine(partido, pred)}</span>` : ''}</summary>
                <div class="prediction-quick playoff-form">
                    <div class="playoff-score-grid">
                        <div class="playoff-score-team">
                            <label for="home-${partido.partido_id}">${partido.equipo_local_nombre}</label>
                            <input id="home-${partido.partido_id}" class="playoff-score-input" type="number" min="0" max="15" inputmode="numeric" data-goals="home" data-match="${partido.partido_id}" value="${homeGoals}">
                        </div>
                        <span class="playoff-score-sep">-</span>
                        <div class="playoff-score-team">
                            <label for="away-${partido.partido_id}">${partido.equipo_visitante_nombre}</label>
                            <input id="away-${partido.partido_id}" class="playoff-score-input" type="number" min="0" max="15" inputmode="numeric" data-goals="away" data-match="${partido.partido_id}" value="${awayGoals}">
                        </div>
                        ${isTie ? `
                            <div class="penalty-choice">
                                <strong>Si empatan, pasa:</strong>
                                <div class="penalty-row">
                                    ${penaltyButton(partido, 'home', partido.equipo_local_nombre, pred)}
                                    ${penaltyButton(partido, 'away', partido.equipo_visitante_nombre, pred)}
                                </div>
                            </div>
                        ` : ''}
                    </div>
                    <button class="save ${ready ? '' : 'pending'}" type="button" data-save="${partido.partido_id}">Guardar prediccion</button>
                </div>
            </details>
        `
    }
    return `
        <details class="box compact prediction-panel"${openAttr}>
            <summary><h3>Mi prediccion</h3>${isCollapsed ? `<span>${predictionLine(partido, pred)}</span>` : ''}</summary>
            <div class="prediction-quick">
                <div class="pick-row">
                    ${pickButton(partido, 'home', `Gana ${partido.equipo_local_nombre}`, pred)}
                    ${pickButton(partido, 'draw', 'Empate', pred)}
                    ${pickButton(partido, 'away', `Gana ${partido.equipo_visitante_nombre}`, pred)}
                </div>
                <button class="save ${pred?.pick ? '' : 'pending'}" type="button" data-save="${partido.partido_id}">Guardar</button>
            </div>
        </details>
    `
}

function pickButton(partido, value, label, pred) {
    return `<button class="pick ${pred?.pick === value ? 'selected' : ''}" type="button" data-pick="${value}" data-match="${partido.partido_id}">${label}</button>`
}

function penaltyButton(partido, value, label, pred) {
    return `<button class="penalty-pick ${pred?.penaltyWinner === value ? 'selected' : ''}" type="button" data-penalty="${value}" data-match="${partido.partido_id}">${label}</button>`
}

function renderDayGroup(key, list) {
    return `
        <details data-accordion="date">
            <summary><div class="fold-head"><strong>${dateTitle(key)}</strong><span>${list.length} partidos</span></div></summary>
            <div class="fold-body">${sortRelevantMatches(list).map(p => renderMatchCard(p)).join('')}</div>
        </details>
    `
}

function renderGroupFold(groupId, list) {
    const group = S.groups.find((g) => g.id === groupId)
    return `
        <details data-accordion="group">
            <summary>
                <div class="fold-head">
                    <strong>Grupo ${groupId}</strong>
                    <span class="fold-flags">${(group?.teams || []).map(renderMiniFlag).join('')}</span>
                </div>
            </summary>
            <div class="fold-body">${sortRelevantMatches(list).map(p => renderMatchCard(p)).join('')}</div>
        </details>
    `
}

function renderEvolutionGroups() {
    return `
        <div class="section-title"><h2>Grupos</h2><span>tabla real</span></div>
        ${S.groups.map(renderStandingGroup).join('')}
    `
}

function renderStandingGroup(group) {
    return `
        <details data-accordion="standing">
            <summary>
                <div class="fold-head">
                    <strong>Grupo ${group.id}</strong>
                    <span class="fold-flags">${group.teams.map(renderMiniFlag).join('')}</span>
                </div>
            </summary>
            <div class="fold-body">
                <div class="standings-table">
                    <div class="standing-row header"><span>#</span><span>Equipo</span><span>PJ</span><span>GF</span><span>GC</span><span>DG</span><span>PTOS</span></div>
                    ${(S.standings[group.id] || []).map((team, index) => renderStandingRow(team, index)).join('')}
                </div>
            </div>
        </details>
    `
}

function renderStandingRow(team, index) {
    const dg = team.gf - team.gc
    return `
        <div class="standing-row">
            <span>${index + 1}</span>
            <span class="${team.q ? 'qualified' : ''}">${team.code} ${S.names[team.code] || team.code}</span>
            <span>${team.pj}</span>
            <span>${team.gf}</span>
            <span>${team.gc}</span>
            <span>${dg > 0 ? '+' : ''}${dg}</span>
            <span>${team.pts}</span>
        </div>
    `
}

function renderPlayoffSection(fase) {
    const list = S.playoffMatches.filter((p) => p.fase === fase)
    if (!list.length) return ''
    // Orden de la lista (solo presentación): en vivo → programados (próximo primero) →
    // finalizados al fondo. Mismo helper que las vistas "relevantes" (sortRelevantMatches).
    return `<h2 class="phase-title">${phaseMeta[fase].label}</h2>${sortRelevantMatches(list).map(p => renderMatchCard(p)).join('')}`
}

function groupBy(items, fn) {
    return items.reduce((acc, item) => {
        const key = fn(item)
        acc[key] = acc[key] || []
        acc[key].push(item)
        return acc
    }, {})
}

function renderAll() {
    const root = S.root
    root.getElementById('subpanel-evolution-groups').innerHTML = renderEvolutionGroups()
    // subpanel-evolution-bracket se puebla lazy con renderWorldBracket()

    const byDate = groupBy(S.groupMatches, dateKey)
    root.getElementById('subpanel-date').innerHTML = Object.entries(byDate).map(([key, list]) => renderDayGroup(key, list)).join('')

    const byGroup = groupBy(S.groupMatches, (p) => p.grupo_mundial)
    root.getElementById('subpanel-group').innerHTML = Object.entries(byGroup).map(([key, list]) => renderGroupFold(key, list)).join('')

    const playoffPanelMap = {
        '16avos': 'subpanel-playoff-16avos',
        octavos: 'subpanel-playoff-octavos',
        cuartos: 'subpanel-playoff-cuartos',
        semifinal: 'subpanel-playoff-semifinal',
        final: 'subpanel-playoff-final'
    }
    Object.entries(playoffPanelMap).forEach(([fase, panelId]) => {
        root.getElementById(panelId).innerHTML = renderPlayoffSection(fase)
    })
    // H4 migración: wirea cada card de playoff del componente compartido.
    wireAllProdePlayoffCards()
}

// ─── Llave visual (verbatim del sandbox; mock interno → S.bracketMatches) ───

function renderWorldBracket(contenedor) {
    const phases = ['16avos', 'octavos', 'cuartos', 'semifinal', 'final']
    const phaseLabels = { '16avos': '16avos', octavos: 'Octavos', cuartos: 'Cuartos', semifinal: 'Semis', final: 'Final' }
    // H3 fix-3: los labels son siempre los largos en todos los anchos. El
    // fix-1 (data-short con "8vos"/"4tos") y el fix-2 ("8"/"4"/"F") quedaron
    // afuera — el mobile cabe con nombres largos si se aprieta padding +
    // gap + font del propio CSS de la llave (Shadow DOM aislado).
    const matches = S.bracketMatches
    const byNumber = Object.fromEntries(matches.map((match) => [match.n, match]))
    let focus = '16avos'

    contenedor.innerHTML = `
        <div class="world-bracket">
            <div class="world-bracket-tabs">
                ${phases.map((fase) => `<button class="world-bracket-tab" type="button" data-world-phase="${fase}">${phaseLabels[fase]}</button>`).join('')}
            </div>
            <div class="world-zoom-controls" aria-label="Zoom de llave">
                <button class="world-zoom-btn" type="button" data-zoom="out">-</button>
                <span class="world-zoom-value" data-zoom-value>100%</span>
                <button class="world-zoom-btn" type="button" data-zoom="in">+</button>
                <button class="world-zoom-btn fit" type="button" data-zoom="fit">Ver completa</button>
            </div>
            <div class="world-bracket-stage">
                <div class="world-bracket-scale">
                    <div class="world-bracket-inner">
                        <svg class="world-bracket-svg"></svg>
                        <div class="world-bracket-cards"></div>
                    </div>
                </div>
            </div>
        </div>
    `

    const stage = contenedor.querySelector('.world-bracket-stage')
    const scaleWrap = contenedor.querySelector('.world-bracket-scale')
    let inner = contenedor.querySelector('.world-bracket-inner')
    let svg = contenedor.querySelector('.world-bracket-svg')
    let cards = contenedor.querySelector('.world-bracket-cards')
    const zoomValue = contenedor.querySelector('[data-zoom-value]')
    // H3 fix-4 (26-may-2026): zoom inicial 34% para que la llave entre completa
    // en la primera vista. Sin persistencia — al salir y volver a Prode el
    // zoom vuelve a 34%. Botones -/+ y "Ver completa" siguen funcionando.
    let zoom = 0.34
    function teamMarkup(team, state) {
        const classes = ['world-team', team.type, state].filter(Boolean).join(' ')
        if (team.type === 'placeholder') return `<div class="${classes}"><span class="world-name">${team.name}</span></div>`
        // Bandera = imagen real de la base (equipo_*_bandera_url), igual que las cards de
        // home/PRODE. Si la URL viene vacía/null, fallback al cuadradito CSS .flag-${code}
        // de siempre (gradiente si el código lo tiene, o cuadro vacío) — no rompe.
        const flag = team.bandera_url
            ? `<img class="world-flag" src="${team.bandera_url}" alt="" aria-hidden="true">`
            : `<span class="world-flag flag-${team.code}" aria-hidden="true"></span>`
        return `<div class="${classes}">${flag}<span class="world-name">${team.name}</span></div>`
    }
    function cardMarkup(match) {
        const homeState = match.winner ? (match.winner === 'home' ? 'winner' : 'loser') : ''
        const awayState = match.winner ? (match.winner === 'away' ? 'winner' : 'loser') : ''
        const score = match.score ? `${match.score}${match.note ? ` - ${match.note}` : ''}` : (match.status || 'Por jugar')
        const scoreClass = match.score ? 'world-score' : 'world-score pending'
        return `
            <button class="world-card ${match.fase === focus ? 'focus' : ''} ${match.fase === 'final' ? 'final' : ''}" type="button" data-world-card="${match.n}" style="top:${match.y}px">
                <div class="world-card-meta"><span class="${scoreClass}">${score}</span></div>
                ${teamMarkup(match.home, homeState)}
                ${teamMarkup(match.away, awayState)}
                <span class="world-match-number">P${match.n}</span>
            </button>
        `
    }
    function assignBracketPositions() {
        const cardHeight = 54
        const baseGap = 10
        const firstRoundStep = cardHeight + baseGap
        const byId = Object.fromEntries(matches.map((match) => [match.n, match]))

        phases.forEach((fase) => {
            matches
                .filter((match) => match.fase === fase)
                .forEach((match, index) => {
                    if (!match.from) {
                        match.y = index * firstRoundStep
                        return
                    }
                    const sources = match.from.map((source) => byId[source]).filter(Boolean)
                    if (sources.length !== 2 || !sources.every((source) => Number.isFinite(source.y))) {
                        match.y = index * firstRoundStep
                        return
                    }
                    const sourceCenterA = sources[0].y + cardHeight / 2
                    const sourceCenterB = sources[1].y + cardHeight / 2
                    match.y = Math.round((sourceCenterA + sourceCenterB) / 2 - cardHeight / 2)
                })
        })

        return Math.max(...matches.map((match) => match.y + cardHeight), 0)
    }
    function columnMarkup(fase) {
        const phaseMatches = matches.filter((match) => match.fase === fase)
        return `
            <section class="world-column phase-${fase}">
                <div class="world-bracket-phase ${fase === focus ? 'focus' : ''}">${phaseLabels[fase]}</div>
                <div class="world-column-matches">
                    ${phaseMatches.map(cardMarkup).join('')}
                </div>
            </section>
        `
    }
    function render() {
        const boardHeight = assignBracketPositions()
        svg.innerHTML = ''
        cards.innerHTML = ''
        inner.innerHTML = '<svg class="world-bracket-svg"></svg>' + phases.map(columnMarkup).join('')
        svg = inner.querySelector('.world-bracket-svg')
        inner.querySelectorAll('.world-column-matches').forEach((column) => {
            column.style.height = `${boardHeight}px`
        })
        applyZoom()
        contenedor.querySelectorAll('[data-world-phase]').forEach((button) => button.classList.toggle('active', button.dataset.worldPhase === focus))
        setTimeout(() => {
            const activeColumn = contenedor.querySelector(`.phase-${focus}`)
            if (activeColumn) stage.scrollLeft = Math.max(0, activeColumn.offsetLeft - 12)
            const firstFocusedMatch = matches.find((match) => match.fase === focus)
            if (firstFocusedMatch && Number.isFinite(firstFocusedMatch.y)) {
                stage.scrollTop = Math.max(0, firstFocusedMatch.y - 120)
            }
        }, 20)
    }
    function applyZoom() {
        const rawWidth = inner.scrollWidth
        const rawHeight = inner.scrollHeight
        scaleWrap.style.width = `${Math.ceil(rawWidth * zoom)}px`
        scaleWrap.style.height = `${Math.ceil(rawHeight * zoom)}px`
        inner.style.transform = `scale(${zoom})`
        inner.style.transformOrigin = 'top left'
        zoomValue.textContent = `${Math.round(zoom * 100)}%`
    }
    function setZoom(nextZoom, keepScroll = true) {
        const oldZoom = zoom
        const centerX = stage.scrollLeft + stage.clientWidth / 2
        const centerY = stage.scrollTop + stage.clientHeight / 2
        zoom = Math.max(.18, Math.min(1.6, Number(nextZoom.toFixed(2))))
        applyZoom()
        if (keepScroll && oldZoom > 0) {
            stage.scrollLeft = Math.max(0, centerX * (zoom / oldZoom) - stage.clientWidth / 2)
            stage.scrollTop = Math.max(0, centerY * (zoom / oldZoom) - stage.clientHeight / 2)
        }
    }
    function fitZoom() {
        const rawWidth = inner.scrollWidth
        const rawHeight = inner.scrollHeight
        const fitWidth = (stage.clientWidth - 4) / rawWidth
        const fitHeight = (stage.clientHeight - 4) / rawHeight
        setZoom(Math.min(1, fitWidth, fitHeight), false)
        stage.scrollLeft = 0
        stage.scrollTop = 0
    }
    contenedor.querySelector('.world-bracket-tabs').addEventListener('click', (event) => {
        const button = event.target.closest('[data-world-phase]')
        if (!button) return
        focus = button.dataset.worldPhase
        render()
    })
    contenedor.querySelector('.world-zoom-controls').addEventListener('click', (event) => {
        const button = event.target.closest('[data-zoom]')
        if (!button) return
        if (button.dataset.zoom === 'in') setZoom(zoom + .1)
        if (button.dataset.zoom === 'out') setZoom(zoom - .1)
        if (button.dataset.zoom === 'fit') fitZoom()
    })
    stage.addEventListener('click', (event) => {
        const card = event.target.closest('[data-world-card]')
        if (!card) return
        const match = byNumber[Number(card.dataset.worldCard)]
        if (match && match.fase !== focus) {
            focus = match.fase
            render()
        }
    })
    let resizeTimer
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(render, 120)
    })
    render()
}

// ─── Event listeners (verbatim del sandbox; document.body → S.root) ─────────

function bindEvents() {
    const root = S.root

    root.querySelector('.tabs').addEventListener('click', (event) => {
        const btn = event.target.closest('[data-tab]')
        if (!btn) return
        root.querySelectorAll('.tab').forEach((tab) => tab.classList.toggle('active', tab === btn))
        root.querySelectorAll('.panel').forEach((panel) => panel.classList.remove('active'))
        root.getElementById(`panel-${btn.dataset.tab}`).classList.add('active')
    })

    root.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-subtab]')
        if (!btn) return
        const panel = btn.closest('.panel')
        panel.querySelectorAll('.subtab').forEach((tab) => tab.classList.toggle('active', tab === btn))
        panel.querySelectorAll('.subpanel').forEach((subpanel) => subpanel.classList.remove('active'))
        root.getElementById(`subpanel-${btn.dataset.subtab}`).classList.add('active')
        if (btn.dataset.subtab === 'evolution-bracket' && !S.bracketRendered) {
            S.bracketRendered = true
            renderWorldBracket(root.getElementById('contenedor-llave'))
        }
    })

    root.addEventListener('toggle', (event) => {
        const current = event.target
        if (!(current instanceof HTMLDetailsElement) || !current.open) return
        const accordion = current.dataset.accordion
        if (!accordion) return
        root.querySelectorAll(`details[data-accordion="${accordion}"]`).forEach((item) => {
            if (item !== current) item.open = false
        })
    }, true)

    root.addEventListener('click', (event) => {
        const matchSummary = event.target.closest('.match > summary')
        if (matchSummary && !event.target.closest('button')) {
            event.preventDefault()
            return
        }
        const openBtn = event.target.closest('[data-open]')
        if (openBtn) {
            event.preventDefault()
            S.collapsed.delete(openBtn.dataset.open)
            refreshMatchCardElement(openBtn.closest('.match'), true)
            return
        }
        const scoreBtn = event.target.closest('[data-score]')
        if (scoreBtn) {
            event.preventDefault()
            const id = scoreBtn.dataset.score
            if (S.openScoring.has(id)) {
                S.openScoring.delete(id)
            } else {
                S.openScoring.add(id)
            }
            // Cards de grupo viven en <details.match>, las de playoff en <div.ppc-wrap>.
            refreshMatchCardElement(scoreBtn.closest('.match, .ppc-wrap'), true)
            return
        }
        const pick = event.target.closest('[data-pick]')
        if (pick) {
            const id = pick.dataset.match
            const card = pick.closest('.match')
            card?.querySelectorAll(`[data-match="${id}"][data-pick]`).forEach((btn) => btn.classList.remove('selected'))
            pick.classList.add('selected')
            card?.querySelector(`[data-save="${id}"]`)?.classList.remove('pending')
            S.predictions[id] = S.predictions[id] || {}
            S.predictions[id].pick = pick.dataset.pick
            S.predictions[id].label = pick.textContent.trim()
            return
        }
        const penalty = event.target.closest('[data-penalty]')
        if (penalty) {
            const id = penalty.dataset.match
            S.predictions[id] = S.predictions[id] || {}
            S.predictions[id].penaltyWinner = penalty.dataset.penalty
            S.predictions[id].pick = penalty.dataset.penalty
            S.predictions[id].label = playoffPredictionLabel(findMatch(id), S.predictions[id])
            refreshMatchCardElement(penalty.closest('.match'), true)
            return
        }
        const save = event.target.closest('[data-save]')
        if (save) {
            // F1.3.c2 — hook de persistencia: handleSave finaliza la
            // predicción en S, la guarda en Postgres y recién entonces
            // colapsa la tarjeta.
            handleSave(save.dataset.save, save.closest('.match'))
            return
        }
    })

    root.addEventListener('input', (event) => {
        const input = event.target.closest('[data-goals]')
        if (!input) return
        const id = input.dataset.match
        S.predictions[id] = S.predictions[id] || {}
        const wasTie = S.predictions[id].goalsHome !== '' &&
            S.predictions[id].goalsAway !== '' &&
            S.predictions[id].goalsHome !== undefined &&
            S.predictions[id].goalsAway !== undefined &&
            Number(S.predictions[id].goalsHome) === Number(S.predictions[id].goalsAway)
        const value = input.value === '' ? '' : Math.max(0, Math.min(15, Number(input.value)))
        input.value = value
        if (input.dataset.goals === 'home') S.predictions[id].goalsHome = value
        if (input.dataset.goals === 'away') S.predictions[id].goalsAway = value
        const partido = findMatch(id)
        const isTie = S.predictions[id].goalsHome !== '' &&
            S.predictions[id].goalsAway !== '' &&
            S.predictions[id].goalsHome !== undefined &&
            S.predictions[id].goalsAway !== undefined &&
            Number(S.predictions[id].goalsHome) === Number(S.predictions[id].goalsAway)
        if (S.predictions[id].goalsHome !== '' && S.predictions[id].goalsAway !== '') {
            const homeGoals = Number(S.predictions[id].goalsHome)
            const awayGoals = Number(S.predictions[id].goalsAway)
            if (homeGoals > awayGoals) S.predictions[id].pick = 'home'
            if (awayGoals > homeGoals) S.predictions[id].pick = 'away'
            if (homeGoals !== awayGoals) delete S.predictions[id].penaltyWinner
        }
        S.predictions[id].label = playoffPredictionLabel(partido, S.predictions[id])
        const card = input.closest('.match')
        card?.querySelector(`[data-save="${id}"]`)?.classList.toggle('pending', !playoffPredictionReady(S.predictions[id]))
        if (wasTie !== isTie) refreshMatchCardElement(card, true)
    })

    root.addEventListener('change', (event) => {
        const select = event.target.closest('[data-field="winner"]')
        if (!select) return
        select.closest('.match')?.querySelector(`[data-save="${select.dataset.match}"]`)?.classList.toggle('pending', !select.value)
    })
}

// ─── F1.3.c2 · persistencia de predicciones en Postgres ─────────────────────

// Siembra S.predictions desde las filas de la tabla `predicciones`.
// Reconstruye la forma que consume el sandbox:
//   group   → { pick: 'home'|'draw'|'away', label, points }
//   playoff → { goalsHome, goalsAway, penaltyWinner?, pick, label, points }
function seedPredictions(rows, matches) {
    const byId = {}
    for (const m of matches) byId[m.partido_id] = m
    const out = {}
    for (const row of rows || []) {
        const partido = byId[row.partido_id]
        if (!partido) continue
        if (row.prediction_type === 'group_match') {
            // El resultado de grupo está codificado en el score (1-0 / 0-0 / 0-1).
            const sl = row.score_local
            const sv = row.score_visitante
            const pick = sl > sv ? 'home' : sl < sv ? 'away' : 'draw'
            const label = pick === 'home' ? `Gana ${partido.equipo_local_nombre}`
                : pick === 'away' ? `Gana ${partido.equipo_visitante_nombre}` : 'Empate'
            out[row.partido_id] = { pick, label, points: row.puntos_obtenidos ?? 0 }
        } else {
            const pred = {
                goalsHome: row.score_local ?? '',
                goalsAway: row.score_visitante ?? '',
                points: row.puntos_obtenidos ?? 0,
            }
            if (row.clasificado_codigo) {
                pred.penaltyWinner = row.clasificado_codigo === partido.equipo_local_codigo ? 'home' : 'away'
                pred.pick = pred.penaltyWinner
            }
            if (Number(pred.goalsHome) > Number(pred.goalsAway)) pred.pick = 'home'
            if (Number(pred.goalsAway) > Number(pred.goalsHome)) pred.pick = 'away'
            pred.label = playoffPredictionLabel(partido, pred)
            out[row.partido_id] = pred
        }
    }
    return out
}

// Codifica la predicción de S.predictions al shape de la tabla `predicciones`
// y la guarda vía el RPC guardar_prediccion. Devuelve { data } | { error }.
async function persistPrediction(id) {
    const partido = findMatch(id)
    const pred = S.predictions[id]
    if (!partido || !pred) return { error: { message: 'estado_invalido' } }
    if (!S.playerId) return { error: { message: 'sin_jugador' } }

    if (partido.fase === 'grupo') {
        // group_match: el pick (home/draw/away) se codifica como score canónico.
        const [scoreLocal, scoreVisitante] = pred.pick === 'home' ? [1, 0]
            : pred.pick === 'away' ? [0, 1]
            : [0, 0]
        return upsertPrediccion(S.playerId, id, {
            prediction_type: 'group_match',
            score_local: scoreLocal,
            score_visitante: scoreVisitante,
            clasificado_codigo: null,
        })
    }
    // playoff_match: goles + clasificado por penales si la predicción es empate.
    const golesLocal = Number(pred.goalsHome)
    const golesVisitante = Number(pred.goalsAway)
    const clasificado = golesLocal === golesVisitante
        ? (pred.penaltyWinner === 'home' ? partido.equipo_local_codigo : partido.equipo_visitante_codigo)
        : null
    return upsertPrediccion(S.playerId, id, {
        prediction_type: 'playoff_match',
        score_local: golesLocal,
        score_visitante: golesVisitante,
        clasificado_codigo: clasificado,
    })
}

// Errores crudos del RPC guardar_prediccion → mensaje de UX.
function traducirErrorPrediccion(error) {
    const m = (error?.message || '').toLowerCase()
    if (m.includes('partido_no_programado')) return 'El partido ya empezó: no se puede predecir ni editar.'
    if (m.includes('prediccion_bloqueada')) return 'Tu predicción quedó bloqueada para este partido.'
    if (m.includes('clasificado_requerido')) return 'En un empate de playoff tenés que elegir quién pasa.'
    if (m.includes('clasificado_invalido')) return 'El equipo que pasa no es válido para este partido.'
    if (m.includes('equipos_sin_definir')) return 'Todavía no están definidos los equipos de este partido.'
    if (m.includes('no_sos_este_jugador') || m.includes('sin_jugador')) return 'No pudimos identificar tu jugador en el grupo.'
    if (m.includes('partido_inexistente')) return 'No encontramos el partido.'
    if (m.includes('tipo')) return 'El tipo de predicción no es válido.'
    if (m.includes('goles')) return 'Cargá un resultado válido.'
    if (m.includes('no_autenticado')) return 'Tu sesión expiró. Recargá la página.'
    return 'No pudimos guardar la predicción. Probá de nuevo.'
}

// Hook del botón "Guardar": finaliza la predicción en S (lógica verbatim del
// sandbox), la persiste en Postgres y recién entonces colapsa la tarjeta. Si
// la persistencia falla, no colapsa y avisa.
async function handleSave(id, card) {
    const partido = findMatch(id)
    S.predictions[id] = S.predictions[id] || {}
    if (partido?.fase !== 'grupo') {
        if (!playoffPredictionReady(S.predictions[id])) return
        S.predictions[id].label = playoffPredictionLabel(partido, S.predictions[id])
        if (Number(S.predictions[id].goalsHome) > Number(S.predictions[id].goalsAway)) S.predictions[id].pick = 'home'
        if (Number(S.predictions[id].goalsAway) > Number(S.predictions[id].goalsHome)) S.predictions[id].pick = 'away'
    } else {
        const select = card?.querySelector(`[data-match="${id}"][data-field="winner"]`)
        if (select?.value) {
            S.predictions[id].pick = select.value
            S.predictions[id].label = select.options[select.selectedIndex].textContent.trim()
        }
        if (!S.predictions[id].label) S.predictions[id].label = 'Prediccion guardada'
        if (!S.predictions[id].pick) return
    }

    const saveBtn = card?.querySelector(`[data-save="${id}"]`)
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Guardando…' }
    const r = await persistPrediction(id)
    if (r.error) {
        S.collapsed.delete(id)
        refreshMatchCardElement(card, true)
        alert('⚠️ ' + traducirErrorPrediccion(r.error))
        return
    }
    S.collapsed.add(id)
    refreshOtherMatchCards(id, card)
    refreshMatchCardElement(card, true)
}



